import { StyleSheet, css } from 'aphrodite'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CompanyLogoUpload from '../components/CompanyLogoUpload'
import GooglePlacesSelect from '../components/GooglePlacesSelect'
import MultiSelect, { type Option } from '../components/MultiSelect'
import DriverInvoiceItemsTable from '../components/invoice/DriverInvoiceItemsTable'
import { useTagContext } from '../contexts/TagContext'
import { useUser } from '../contexts/UserContext'
import { getUserService } from '../services/authService'
import { invoiceService } from '../services/invoiceService'
import { type LoadResponse, loadService } from '../services/loadService'
import { projectService } from '../services/projectService'
import {
  getRelatedBrokerName,
  getRelatedContractorName,
  tagService,
} from '../services/tagService'
import { truckService } from '../services/truckService'
import { userService } from '../services/userService'
import { workOrderService } from '../services/workOrderService'
import type {
  CreateInvoiceData,
  DriverInvoiceItem,
  InvoiceItem,
} from '../types/invoice'
import { isDriverInvoiceItem } from '../types/invoice'
import type { Tag } from '../types/tag'
import type { Contact, User } from '../types/user'
import { UserType } from '../types/user'
import type { WorkOrder, WorkOrderReportTag } from '../types/workorder'

type GooglePlaceOption = { label: string; value: string }

interface CreateDriverInvoicePageProps {
  invoiceType?: 'DRIVER' | 'SUB_HAULER'
}

const CreateDriverInvoicePage: React.FC<CreateDriverInvoicePageProps> = ({
  invoiceType = 'DRIVER',
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [tags, setTags] = useState<(Tag | WorkOrderReportTag)[]>([])
  const [originalInvoiceData, setOriginalInvoiceData] =
    useState<CreateInvoiceData | null>(null)
  const [invoice, setInvoice] = useState<CreateInvoiceData>({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    bill_to_user_id: 0,
    company_name: '',
    company_address: '',
    items: [],
    invoice_type: invoiceType,
  })
  const [contractorNames, setContractorNames] = useState<
    Record<number, string>
  >({})
  const [brokerNames, setBrokerNames] = useState<Record<number, string>>({})
  const [rateTypeByTagId, setRateTypeByTagId] = useState<
    Record<number, string>
  >({})
  const [user, setUser] = useState<User | null>(null)
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null)
  const [userTruckNumber, setUserTruckNumber] = useState<string>('')
  const { user: userContext } = useUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<GooglePlaceOption | null>(
    null,
  )
  const { tags: contextTags, refreshTags } = useTagContext()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [workorders, setWorkorders] = useState<WorkOrder[]>([])
  const [selectedWorkorderId, setSelectedWorkorderId] = useState<
    number | undefined
  >(undefined)
  const [drivers, setDrivers] = useState<User[]>([])
  const [selectedDriverId, setSelectedDriverId] = useState<number | undefined>(
    undefined,
  )
  const [preservedTags, setPreservedTags] = useState<Tag[]>([])
  const [selectedTagCache, setSelectedTagCache] = useState<
    Record<number, { id: number; label: string }>
  >({})
  const [isLoadingPreloadedTags, setIsLoadingPreloadedTags] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      // Check if we have preloaded tags - if so, show loading state
      const preloadedTagsCheck = location.state?.preloadedTags as
        | WorkOrderReportTag[]
        | undefined
      if (preloadedTagsCheck && preloadedTagsCheck.length > 0) {
        setIsLoadingPreloadedTags(true)
      }
      try {
        // Check if we have preloaded tags from navigation state
        const preloadedTags = location.state?.preloadedTags as
          | WorkOrderReportTag[]
          | undefined

        if (preloadedTags && preloadedTags.length > 0) {
          // Use preloaded tags
          setTags(preloadedTags)

          // Use contractor and broker info from tag report data
          const contractorNamesMap: Record<number, string> = {}
          const brokerNamesMap: Record<number, string> = {}
          for (const tag of preloadedTags) {
            console.log('Tag data for invoice:', {
              id: tag.id,
              contractor: tag.contractor,
              broker: tag.broker,
              truck_number: tag.truck_number,
              weight: tag.weight,
              volume: tag.volume,
            })

            // Use contractor info from tag report if available
            if (tag.contractor) {
              contractorNamesMap[tag.id] =
                `${tag.contractor.first_name} ${tag.contractor.last_name}`
            } else {
              contractorNamesMap[tag.id] = '-'
            }

            // Use broker info from tag report if available
            if (tag.broker) {
              brokerNamesMap[tag.id] =
                `${tag.broker.first_name} ${tag.broker.last_name}`
            } else {
              brokerNamesMap[tag.id] = '-'
            }
          }
          console.log('Contractor names map:', contractorNamesMap)
          console.log('Broker names map:', brokerNamesMap)
          console.log(
            'Setting contractor names for tags:',
            Object.keys(contractorNamesMap),
          )
          console.log(
            'Setting broker names for tags:',
            Object.keys(brokerNamesMap),
          )
          setContractorNames(contractorNamesMap)
          setBrokerNames(brokerNamesMap)

          // Pre-populate invoice items with completed tags
          const preloadedItems: DriverInvoiceItem[] = []
          for (const tag of preloadedTags) {
            try {
              const loadCount = await tagService.getLoadCount(tag.id.toString())
              // Use total_hours directly from WorkOrderReportTag
              const totalTime = tag.total_hours || 0

              // Fetch workorder for rate_type and pay_rate if available
              let workorder: WorkOrder | null = null
              if (tag.workorder_id) {
                try {
                  workorder = await workOrderService.getWorkOrder(
                    tag.workorder_id,
                  )
                } catch (e) {
                  console.warn('Failed to fetch workorder for tag:', tag.id, e)
                }
              }

              // Resolve rate: tag.pay_rate (set from dispatch) > workorder > fallback 0
              const computedRate =
                Number(tag.pay_rate) || Number(workorder?.pay_rate) || 0
              const computedHours = Number(totalTime) || 0
              const computedBrokerFee = 0

              // Fetch related project to determine rate_type (fallback)
              let projectRateType: string | undefined
              try {
                const pid: number | undefined = ((): number | undefined => {
                  const anyTag = tag as unknown as {
                    project_id?: number
                    project?: { id?: number }
                  }
                  if (typeof anyTag.project_id === 'number')
                    return anyTag.project_id
                  if (anyTag.project && typeof anyTag.project.id === 'number')
                    return anyTag.project.id
                  return undefined
                })()
                if (pid) {
                  const proj = await projectService.getProject(Number(pid))
                  projectRateType = proj?.rate_type
                }
              } catch (e) {
                // ignore project fetch failure
              }

              // Resolve rate_type: workorder > project
              const resolvedRateType = String(
                workorder?.rate_type || projectRateType || '',
              ).toUpperCase()
              setRateTypeByTagId((prev) => ({
                ...prev,
                [tag.id]: String(workorder?.rate_type || projectRateType || ''),
              }))

              const isLoadBased = resolvedRateType === 'LOAD'
              const loadCountNum = Number(loadCount) || 0
              const baseTotal = isLoadBased
                ? loadCountNum * computedRate
                : computedHours * computedRate
              const computedTotal = Math.max(0, baseTotal - computedBrokerFee)

              const preloadedItem: DriverInvoiceItem = {
                id: -Math.floor(Math.random() * 1000000),
                tag_id: tag.id,
                rate: computedRate,
                hours: computedHours,
                job_site: tag.origin_name || '',
                truck_number: tag.truck_number || userTruckNumber || '',
                date: tag.start_date ? tag.start_date.split('T')[0] : undefined,
                material: tag.material || '',
                volume: tag.total_weight || tag.weight || tag.volume || 0,
                broker_fee: 0,
                number_of_loads: Number(loadCount).toString() || '0',
                total: computedTotal.toFixed(2),
              }
              console.log('Preloaded item:', preloadedItem)
              preloadedItems.push(preloadedItem)
            } catch (error) {
              console.error(`Failed to process tag ${tag.id}:`, error)
            }
          }

          // Update invoice with preloaded items
          setInvoice((prev) => ({
            ...prev,
            items: preloadedItems,
          }))
        } else {
          // Normal flow - fetch tags with optional filters from query string
          const usp = new URLSearchParams(location.search)
          const workorderIdParam = usp.get('workorder_id')
          const assignedUserIdParam = usp.get('assigned_user_id')
          if (workorderIdParam) setSelectedWorkorderId(Number(workorderIdParam))
          else setSelectedWorkorderId(undefined)
          if (assignedUserIdParam)
            setSelectedDriverId(Number(assignedUserIdParam))
          else setSelectedDriverId(undefined)
          const objects = await invoiceService.getDriverObjects({
            workorder_id: workorderIdParam
              ? Number(workorderIdParam)
              : undefined,
            assigned_user_id: assignedUserIdParam
              ? Number(assignedUserIdParam)
              : undefined,
          })
          setTags(objects)

          // Seed cache from API payload
          setSelectedTagCache((prev) => {
            const next = { ...prev }
            for (const t of objects) {
              next[t.id] = { id: t.id, label: t.label }
            }
            return next
          })

          // Derive contractor and broker names directly from API response when available
          const contractorNamesMap: Record<number, string> = {}
          const brokerNamesMap: Record<number, string> = {}
          type DriverObjectShape = Tag & {
            contractor?: { first_name?: string; last_name?: string }
            broker?: { first_name?: string; last_name?: string }
            project?: {
              created_by?: { first_name?: string; last_name?: string }
            }
            workorder?: {
              created_by?: { first_name?: string; last_name?: string }
              project?: {
                created_by?: { first_name?: string; last_name?: string }
              }
            }
          }
          for (const raw of objects as DriverObjectShape[]) {
            const id = raw.id as number
            // Contractor: prefer explicit contractor object; fallback to project.created_by
            const contractorObj =
              raw.contractor ||
              raw.project?.created_by ||
              raw.workorder?.project?.created_by
            const contractorName = contractorObj
              ? `${contractorObj.first_name || ''} ${contractorObj.last_name || ''}`.trim()
              : '-'
            contractorNamesMap[id] =
              contractorName && contractorName !== '' ? contractorName : '-'

            // Broker: prefer explicit broker object; fallback to workorder.created_by
            const brokerObj = raw.broker || raw.workorder?.created_by
            const brokerName = brokerObj
              ? `${brokerObj.first_name || ''} ${brokerObj.last_name || ''}`.trim()
              : '-'
            brokerNamesMap[id] =
              brokerName && brokerName !== '' ? brokerName : '-'
          }
          // Merge so previously selected tags retain names even if not in current filter
          setContractorNames((prev) => ({ ...prev, ...contractorNamesMap }))
          setBrokerNames((prev) => ({ ...prev, ...brokerNamesMap }))
        }
      } catch (error) {
        console.error('Failed to fetch data:', error)
        toast.error('Failed to fetch data. Please try again.')
      } finally {
        setIsLoadingPreloadedTags(false)
      }
    }
    fetchData()
  }, [location.state, userTruckNumber, location.search])

  // Load workorders for filter dropdown
  useEffect(() => {
    const loadWorkorders = async () => {
      try {
        const resp = await workOrderService.getWorkOrders({ page_size: 1000 })
        setWorkorders(resp.items)
      } catch (e) {
        console.error('Failed to load workorders for filter UI', e)
      }
    }
    loadWorkorders()
  }, [])

  // Load same-company drivers for filter dropdown
  useEffect(() => {
    const loadDrivers = async () => {
      try {
        const company = await userService.getUserCompany()
        if (company?.id) {
          const result = await userService.getUsersByCompanyAndType(
            company.id,
            UserType.DRIVER,
          )
          setDrivers(result)
        } else {
          setDrivers([])
        }
      } catch (e) {
        console.error('Failed to load drivers for filter UI', e)
      }
    }
    loadDrivers()
  }, [])

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getUserService()
        if (!response.error && response.data?.user) {
          setUser(response.data.user)
          setCurrentLogoUrl(response.data.user.logo_url || null)

          // Fetch and auto-populate company information
          try {
            const company = await userService.getUserCompany()
            if (company) {
              setInvoice((prev) => ({
                ...prev,
                company_name: company.name || '',
                company_address: company.address || '',
              }))
            }
          } catch (error) {
            console.error('Failed to fetch company information:', error)
            // Continue without company info - not critical
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error)
      }
    }
    fetchUser()
  }, [])

  // Load contacts for Bill To selector
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const contactsList = await userService.getContacts()
        setContacts(contactsList)
      } catch (e) {
        console.error('Failed to load contacts', e)
      }
    }
    loadContacts()
  }, [])

  useEffect(() => {
    const fetchUserTruck = async () => {
      if (user?.id) {
        try {
          const trucks = await truckService.getUserVehicles(user.id)
          if (trucks && trucks.length > 0) {
            setUserTruckNumber(trucks[0].truck_number)
          }
        } catch (error) {
          console.error('Failed to fetch truck info:', error)
        }
      }
    }
    fetchUserTruck()
  }, [user])

  // Preserve already-selected tags in dropdown even when filters change
  useEffect(() => {
    const ensureSelectedTagsPresent = async () => {
      try {
        const currentIds = new Set(tags.map((t) => t.id))
        const preservedIds = new Set(preservedTags.map((t) => t.id))
        const neededIds = Array.from(
          new Set(
            invoice.items
              .filter(isDriverInvoiceItem)
              .map((it) => it.tag_id)
              .filter((id): id is number => typeof id === 'number' && id > 0),
          ),
        ).filter((id) => !currentIds.has(id) && !preservedIds.has(id))

        if (neededIds.length === 0) return

        const fetched = await Promise.all(
          neededIds.map(async (id) => {
            try {
              return await tagService.getTag(String(id))
            } catch (e) {
              console.warn('Failed to fetch missing selected tag', id, e)
              return null
            }
          }),
        )
        const valid = fetched.filter((t): t is Tag => Boolean(t))
        if (valid.length > 0) {
          setPreservedTags((prev) => {
            const byId = new Map(prev.map((t) => [t.id, t] as const))
            for (const t of valid) byId.set(t.id, t)
            return Array.from(byId.values())
          })
        }
      } catch (e) {
        console.warn('Error preserving selected tags', e)
      }
    }
    ensureSelectedTagsPresent()
  }, [invoice.items, tags, preservedTags])

  const addItem = () => {
    const newItem: DriverInvoiceItem = {
      id: -Math.floor(Math.random() * 1000000),
      tag_id: -1,
      rate: 0,
      hours: undefined,
      job_site: undefined,
      truck_number: userTruckNumber || '', // Ensure it's never undefined
      date: undefined,
      material: undefined,
      volume: 0,
      broker_fee: 0,
      total: '0.00',
    }

    setInvoice((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }))
  }

  const updateItem = (
    index: number,
    field: string,
    value: string | number | undefined,
  ) => {
    let newValue: string | number | undefined = value
    if (['hours', 'rate', 'broker_fee'].includes(field)) {
      newValue = value === '' ? undefined : Number(value)
    }
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const next = { ...item, [field]: newValue } as DriverInvoiceItem
          // Only auto-recalculate when changing rate/hours/broker_fee, not when editing total directly
          if (field === 'rate' || field === 'hours' || field === 'broker_fee') {
            const rate = Number((next.rate as number) || 0)
            const hours = Number((next.hours as number) || 0)
            const brokerFee = Number((next.broker_fee as number) || 0)
            const loads = Number(next.number_of_loads || 0)
            const rt = rateTypeByTagId[next.tag_id]
            const useLoads = String(rt || '').toUpperCase() === 'LOAD'
            const base = useLoads ? loads * rate : hours * rate
            const computed = Math.max(0, base - brokerFee)
            next.total = computed.toFixed(2)
          }
          return next
        }
        return item
      }),
    }))
  }

  const handleObjectSelect = async (index: number, objectId: number) => {
    if (objectId === -1) return

    const selectedTag = tags.find((tag) => tag.id === objectId)
    if (selectedTag) {
      try {
        const loadCount = await tagService.getLoadCount(
          selectedTag.id.toString(),
        )

        // Handle both Tag and WorkOrderReportTag types
        let totalTime: number
        if ('total_hours' in selectedTag) {
          // WorkOrderReportTag
          totalTime = selectedTag.total_hours || 0
        } else {
          // Tag
          totalTime = await tagService.calculateTotalTime(
            selectedTag.id.toString(),
            selectedTag,
          )
        }

        const formattedDate =
          'created_date' in selectedTag && selectedTag.created_date
            ? new Date(selectedTag.created_date).toISOString().split('T')[0]
            : 'start_date' in selectedTag && selectedTag.start_date
              ? new Date(selectedTag.start_date).toISOString().split('T')[0]
              : undefined

        const loads = await loadService.getLoadsByTag(selectedTag.id.toString())
        const totalWeight = loads.reduce(
          (sum: number, load: LoadResponse) => sum + (Number(load.weight) || 0),
          0,
        )
        const numberOfLoads = String(loads.length || 0)

        // Get truck number from assigned user or use truck_number from WorkOrderReportTag
        let assignedUserTruckNumber = userTruckNumber // fallback to current user's truck

        if ('truck_number' in selectedTag && selectedTag.truck_number) {
          // WorkOrderReportTag has truck_number field
          assignedUserTruckNumber = selectedTag.truck_number
        } else if (selectedTag.assigned_user?.id) {
          // Tag - get truck from assigned user
          try {
            const userVehicles = await truckService.getUserVehicles(
              selectedTag.assigned_user.id,
            )
            if (userVehicles.length > 0) {
              assignedUserTruckNumber = userVehicles[0].truck_number
            }
          } catch (error) {
            console.error('Failed to fetch assigned user truck info:', error)
            // Continue with fallback truck number
          }
        }

        // Resolve rate_type from workorder, then project
        const wo =
          typeof selectedTag.workorder === 'object'
            ? selectedTag.workorder
            : null
        const projectRateType = await (async (): Promise<
          string | undefined
        > => {
          try {
            const pid: number | undefined = ((): number | undefined => {
              const anyTag = selectedTag as unknown as {
                project_id?: number
                project?: { id?: number }
              }
              if (typeof anyTag.project_id === 'number')
                return anyTag.project_id
              if (anyTag.project && typeof anyTag.project.id === 'number')
                return anyTag.project.id
              return undefined
            })()
            if (pid) {
              const proj = await projectService.getProject(Number(pid))
              return proj?.rate_type
            }
          } catch {}
          return undefined
        })()
        const resolvedRateType = String(
          wo?.rate_type || projectRateType || '',
        ).toUpperCase()
        setRateTypeByTagId((prev) => ({
          ...prev,
          [objectId]: String(wo?.rate_type || projectRateType || ''),
        }))

        setInvoice((prev) => ({
          ...prev,
          items: prev.items.map((item, i) => {
            if (i === index) {
              // Resolve pay_rate: workorder > tag
              const computedRate =
                Number(wo?.pay_rate) || Number(selectedTag.pay_rate) || 0
              const computedHours = totalTime
              const computedBrokerFee = Number(
                (item as DriverInvoiceItem).broker_fee || 0,
              )

              const isLoadBased = resolvedRateType === 'LOAD'
              const loadsCount = Number(numberOfLoads) || 0
              const baseTotal = isLoadBased
                ? loadsCount * computedRate
                : computedHours * computedRate
              const computedTotal = Math.max(0, baseTotal - computedBrokerFee)

              const updatedItem: DriverInvoiceItem = {
                ...item,
                tag_id: objectId,
                rate: computedRate,
                hours: computedHours,
                job_site:
                  'origin_name' in selectedTag ? selectedTag.origin_name : '',
                date: formattedDate,
                truck_number: assignedUserTruckNumber,
                material: selectedTag.material || '',
                volume: totalWeight || 0,
                number_of_loads: numberOfLoads,
                total: computedTotal.toFixed(2),
              }
              return updatedItem
            }
            return item
          }),
        }))

        // Lock contractor/broker display names for this selected tag
        try {
          const contractorName = await getRelatedContractorName(
            selectedTag as Tag,
          )
          setContractorNames((prev) => ({
            ...prev,
            [objectId]: contractorName,
          }))
        } catch {}
        try {
          const brokerName = await getRelatedBrokerName(selectedTag as Tag)
          setBrokerNames((prev) => ({ ...prev, [objectId]: brokerName }))
        } catch {}

        // Cache the selected tag option so it persists in the select
        setSelectedTagCache((prev) => ({
          ...prev,
          [objectId]: { id: objectId, label: selectedTag.label },
        }))
      } catch (error) {
        console.error('Error fetching tag data:', error)
        toast.error('Failed to fetch tag data')
      }
    }
  }

  const handleSubmit = async () => {
    // Validate that a logo is uploaded (check both currentLogoUrl and user.logo_url)
    const logoUrl = currentLogoUrl || user?.logo_url
    if (!logoUrl) {
      toast.error('Please upload a company logo before creating the invoice.')
      return
    }

    try {
      setIsSubmitting(true)
      const invoiceData: CreateInvoiceData = {
        ...invoice,
        invoice_type: invoiceType,
        items: invoice.items.filter(isDriverInvoiceItem).map((item) => ({
          ...item,
          tag_id: item.tag_id,
          total: (() => {
            const val =
              typeof item.total === 'string'
                ? item.total
                : String(item.total ?? '0')
            const num = Number.parseFloat(val)
            return (Number.isFinite(num) && num >= 0 ? num : 0).toFixed(2)
          })(),
        })),
      }

      const response = await invoiceService.create(invoiceData)
      setOriginalInvoiceData(invoiceData)
      toast.success('Invoice created successfully!')

      // Redirect to the newly created invoice view page
      navigate(`/invoices/view/${response.id}`)
    } catch (error) {
      console.error('Failed to create invoice:', error)
      toast.error('Failed to create invoice. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLogoUpdate = (logoUrl: string | null) => {
    setCurrentLogoUrl(logoUrl)
  }

  if (isLoadingPreloadedTags) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.loadingContainer)}>
          <i className="fa-solid fa-spinner fa-spin fa-2x" />
          <span className={css(styles.loadingText)}>
            Loading invoice data...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.invoiceContainer)}>
        <div className={css(styles.header)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CompanyLogoUpload
              currentLogoUrl={currentLogoUrl}
              onLogoUpdate={handleLogoUpdate}
              size="medium"
              showRemoveButton={false}
            />
          </div>
          <h1 className={css(styles.invoiceTitle)}>INVOICE</h1>
        </div>

        <div className={css(styles.billingSection)}>
          <div className={css(styles.leftSection)}>
            <div className={css(styles.companySection)}>
              <div className={css(styles.companyHeader)}>Your Company:</div>
              <input
                type="text"
                value={invoice.company_name}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    company_name: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="Your Company"
              />
              <GooglePlacesSelect
                value={
                  invoice.company_address
                    ? {
                        label: invoice.company_address,
                        value: invoice.company_address,
                      }
                    : null
                }
                onChange={(option) =>
                  setInvoice((prev) => ({
                    ...prev,
                    company_address: option?.value || '',
                  }))
                }
                placeholder="Company's Address"
              />
            </div>

            <div className={css(styles.billTo)}>
              <div className={css(styles.billToHeader)}>Bill To</div>
              <MultiSelect
                isMulti={false}
                placeholder="Select contact"
                options={(() => {
                  // Filter contacts that have either user_id or id, and are not the current user
                  const filteredContacts = contacts.filter((c) => {
                    const userId =
                      c.contact_object?.user_id || c.contact_object?.id
                    return userId && userId !== user?.id
                  })
                  const options = filteredContacts.map((c) => {
                    const userId =
                      c.contact_object.user_id || c.contact_object.id
                    // These fields should be available from the backend after the update
                    const companyName =
                      c.contact_object.company_name || 'No Company'
                    const userType = c.contact_object.user_type || 'Unknown'
                    const displayName =
                      c.contact_object.name || c.contact_object.email

                    return {
                      value: String(userId),
                      label: displayName, // Just the name for the main label
                      name: displayName,
                      email: c.contact_object.email,
                      companyName: companyName,
                      userType: userType,
                    } as Option
                  })
                  return options
                })()}
                value={
                  invoice.bill_to_user_id
                    ? (() => {
                        const selectedContact = contacts.find(
                          (c) =>
                            (c.contact_object.user_id ||
                              c.contact_object.id) === invoice.bill_to_user_id,
                        )
                        if (selectedContact) {
                          const companyName =
                            selectedContact.contact_object.company_name ||
                            'No Company'
                          const userType =
                            selectedContact.contact_object.user_type ||
                            'Unknown'
                          const displayName =
                            selectedContact.contact_object.name ||
                            selectedContact.contact_object.email
                          return {
                            value: String(invoice.bill_to_user_id),
                            label: displayName,
                            name: displayName,
                            email: selectedContact.contact_object.email,
                            companyName: companyName,
                            userType: userType,
                          } as Option
                        }
                        return undefined
                      })()
                    : undefined
                }
                onChange={(opt) => {
                  const single = !Array.isArray(opt) ? opt : opt ? opt[0] : null
                  setInvoice((prev) => ({
                    ...prev,
                    bill_to_user_id: single ? Number.parseInt(single.value) : 0,
                  }))
                }}
              />
            </div>
          </div>

          <div className={css(styles.invoiceInfo)}>
            <div className={css(styles.invoiceInfoHeader)}>Invoice Details</div>
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>Invoice Date</span>
              <input
                type="date"
                value={invoice.invoice_date}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    invoice_date: e.target.value,
                  }))
                }
                className={css(styles.input)}
              />
            </div>
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>Due Date</span>
              <input
                type="date"
                value={invoice.due_date}
                onChange={(e) =>
                  setInvoice((prev) => ({ ...prev, due_date: e.target.value }))
                }
                className={css(styles.input)}
              />
            </div>
          </div>
        </div>

        {/* Additional Invoice Details */}
        <div className={css(styles.additionalFieldsSection)}>
          <div className={css(styles.fieldRow)}>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>Summary Description</label>
              <textarea
                value={invoice.summary_description || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    summary_description: e.target.value,
                  }))
                }
                className={css(styles.textarea)}
                placeholder="Brief description of work performed"
                rows={3}
              />
            </div>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>How to Pay</label>
              <textarea
                value={invoice.how_to_pay || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    how_to_pay: e.target.value,
                  }))
                }
                className={css(styles.textarea)}
                placeholder="Payment instructions (e.g., bank details, check address)"
                rows={3}
              />
            </div>
          </div>
          <div className={css(styles.fieldRow)}>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>PO Number</label>
              <input
                type="text"
                value={invoice.purchase_order_number || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    purchase_order_number: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="Purchase order number"
              />
            </div>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>Payment Terms</label>
              <input
                type="text"
                value={invoice.payment_terms_text || ''}
                onChange={(e) =>
                  setInvoice((prev) => ({
                    ...prev,
                    payment_terms_text: e.target.value,
                  }))
                }
                className={css(styles.input)}
                placeholder="e.g., Net 30, Due on receipt"
              />
            </div>
          </div>
        </div>

        <div className={css(styles.itemsTable)}>
          {/* Filter UI */}
          <div className={css(styles.filterBar)}>
            <div className={css(styles.filterGroup)}>
              <label className={css(styles.filterLabel)}>Dispatch</label>
              <MultiSelect
                isMulti={false}
                placeholder="All dispatches"
                options={workorders.map((wo) => ({
                  value: String(wo.id),
                  label: `${wo.label || `Dispatch ${wo.id}`}${wo.material ? ` — ${wo.material}` : ''}`,
                }))}
                value={
                  selectedWorkorderId
                    ? ({
                        value: String(selectedWorkorderId),
                        label: (() => {
                          const w = workorders.find(
                            (ww) => Number(ww.id) === selectedWorkorderId,
                          )
                          return w
                            ? `${w.label || `Dispatch ${w.id}`}${w.material ? ` — ${w.material}` : ''}`
                            : ''
                        })(),
                      } as Option)
                    : null
                }
                onChange={(opt: Option | Option[] | null) => {
                  const single = !Array.isArray(opt) ? opt : opt ? opt[0] : null
                  const val = single ? Number(single.value) : undefined
                  setSelectedWorkorderId(val)
                  const usp = new URLSearchParams(location.search)
                  if (val) usp.set('workorder_id', String(val))
                  else usp.delete('workorder_id')
                  navigate({
                    pathname: location.pathname,
                    search: usp.toString(),
                  })
                }}
              />
            </div>
            <div className={css(styles.filterGroup)}>
              <label className={css(styles.filterLabel)}>Assigned Driver</label>
              <MultiSelect
                isMulti={false}
                placeholder="All drivers"
                options={drivers.map((d) => ({
                  value: String(d.id),
                  label:
                    `${d.first_name || ''} ${d.last_name || ''}`.trim() ||
                    d.email,
                }))}
                value={
                  selectedDriverId
                    ? ({
                        value: String(selectedDriverId),
                        label: (() => {
                          const d = drivers.find(
                            (dd) => dd.id === selectedDriverId,
                          )
                          return d
                            ? `${d.first_name || ''} ${d.last_name || ''}`.trim() ||
                                d.email
                            : ''
                        })(),
                      } as Option)
                    : null
                }
                onChange={(opt: Option | Option[] | null) => {
                  const single = !Array.isArray(opt) ? opt : opt ? opt[0] : null
                  const val = single ? Number(single.value) : undefined
                  setSelectedDriverId(val)
                  const usp = new URLSearchParams(location.search)
                  if (val) usp.set('assigned_user_id', String(val))
                  else usp.delete('assigned_user_id')
                  navigate({
                    pathname: location.pathname,
                    search: usp.toString(),
                  })
                }}
              />
            </div>
            {(selectedWorkorderId || selectedDriverId) && (
              <button
                className={css(styles.clearFiltersButton)}
                title="Clear filters"
                onClick={() => {
                  setSelectedWorkorderId(undefined)
                  setSelectedDriverId(undefined)
                  const usp = new URLSearchParams(location.search)
                  usp.delete('workorder_id')
                  usp.delete('assigned_user_id')
                  navigate({
                    pathname: location.pathname,
                    search: usp.toString(),
                  })
                }}
              >
                <i className="fa-solid fa-eraser" />
                <span>Clear</span>
              </button>
            )}
          </div>
          {(() => {
            const base = [...tags]
            const withPreserved = [
              ...base,
              ...preservedTags.filter(
                (pt) => !base.some((t) => t.id === pt.id),
              ),
            ]
            const cached = Object.values(selectedTagCache)
            const withCached = [
              ...withPreserved,
              ...cached.filter(
                (ct) => !withPreserved.some((t) => t.id === ct.id),
              ),
            ] as Tag[]
            const selectedIds = invoice.items
              .filter(isDriverInvoiceItem)
              .map((i) => i.tag_id)
              .filter((id): id is number => typeof id === 'number' && id > 0)
            return (
              <DriverInvoiceItemsTable
                items={invoice.items.filter(isDriverInvoiceItem)}
                isEditable={true}
                onItemUpdate={updateItem}
                onAddItem={addItem}
                tags={withCached}
                tagsFiltered={tags}
                contractorNames={contractorNames}
                brokerNames={brokerNames}
                userTruckNumber={userTruckNumber}
                onObjectSelect={handleObjectSelect}
                selectedTagIds={selectedIds}
              />
            )
          })()}
        </div>

        <div className={css(styles.actions)}>
          <button
            className={css(styles.submitButton)}
            onClick={handleSubmit}
            disabled={!(currentLogoUrl || user?.logo_url) || isSubmitting}
            title={
              !(currentLogoUrl || user?.logo_url)
                ? 'Please upload a company logo first'
                : ''
            }
          >
            Create Invoice
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
    color: '#1C64F2',
  },
  loadingText: {
    fontSize: '16px',
    color: '#374151',
    fontWeight: 500,
  },
  invoiceContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxWidth: '90%',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
  },
  logoSection: {
    width: '200px',
    height: '100px',
    marginBottom: '24px',
  },
  logoPlaceholder: {
    width: 200,
    height: 100,
    border: '2px dashed #D1D5DB',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    overflow: 'hidden',
    position: 'relative',
    padding: 0,
  },
  invoiceTitle: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    padding: '16px',
  },
  billingSection: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: '40px',
    marginBottom: '40px',
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  companySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  billTo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  billToHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  invoiceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#F9FAFB',
    padding: '24px',
    borderRadius: '8px',
    height: 'fit-content',
  },
  invoiceInfoHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    width: '100px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  itemsTable: {
    marginBottom: '24px',
  },
  filterBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 260,
  },
  filterLabel: {
    fontSize: '12px',
    color: '#374151',
    fontWeight: 600,
  },
  clearFiltersButton: {
    fontSize: '12px',
    height: 38,
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    color: '#6B7280',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition:
      'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      borderColor: '#D1D5DB',
      color: '#374151',
    },
    ':focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.15)',
      borderColor: '#93C5FD',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '24px',
  },
  submitButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#93C5FD',
      cursor: 'not-allowed',
    },
  },

  logoUploadArea: {
    transition: 'border-color 0.2s',
    ':hover': {
      borderColor: '#1C64F2',
      background: '#F3F4F6',
    },
  },
  removeLogoButton: {
    position: 'absolute',
    top: -4,
    right: 4,
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    fontSize: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#374151',
    },
  },
  companyHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  additionalFieldsSection: {
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
})

export default CreateDriverInvoicePage
